import { logger } from "@/lib/logger";
import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Link2,
  Unlink,
  RefreshCw,
  CheckCircle,
  ExternalLink,
  Clock,
  Sparkles,
  AlertTriangle,
  AlertCircle,
  Loader2,
  Lock,
} from "lucide-react";
import { FaMeta, FaMicrosoft, FaLinkedin } from "react-icons/fa6";
import { FcGoogle } from "react-icons/fc";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function authHeaders() { const t = localStorage.getItem("roas_session_token"); return t ? { "Authorization": `Bearer ${t}` } : {}; }

const PLATFORMS = {
  linkedin: {
    name: "LinkedIn Ads",
    description: "LinkedIn Campaign Manager",
    icon: FaLinkedin,
    iconColor: "text-[#0A66C2]",
    buttonClass: "bg-[#0A66C2] text-white hover:bg-[#0A66C2]/90",
    connectedBg: "bg-indigo-500/5 border-indigo-500/20",
    iconBg: "bg-indigo-500/10",
    enabled: true,
  },
  meta: {
    name: "Meta for Business Ads",
    description: "Facebook & Instagram Ads",
    icon: FaMeta,
    iconColor: "text-[#1877F2]",
    buttonClass: "bg-[#1877F2] text-white hover:bg-[#1877F2]/90",
    connectedBg: "bg-blue-500/5 border-blue-500/20",
    iconBg: "bg-blue-500/10",
    enabled: true,
  },
  google: {
    name: "Google Ads",
    description: "Google Search, Display, YouTube & Shopping",
    icon: FcGoogle,
    iconColor: "",
    buttonClass: "bg-white text-black hover:bg-zinc-200",
    connectedBg: "bg-emerald-500/5 border-emerald-500/20",
    iconBg: "bg-emerald-500/10",
    enabled: true,
  },
  bing: {
    name: "Bing Ads",
    description: "Microsoft Advertising Network",
    icon: FaMicrosoft,
    iconColor: "text-[#00A4EF]",
    buttonClass: "bg-[#00A4EF] text-white hover:bg-[#00A4EF]/90",
    connectedBg: "bg-sky-500/5 border-sky-500/20",
    iconBg: "bg-sky-500/10",
    enabled: true,
  },
};

const OAUTH_ERROR_LABELS = {
  token_exchange_failed: "authorization could not be completed — please try connecting again",
  no_access_token: "the provider did not return an access token — please try again",
  invalid_state: "the session expired during sign-in — please try connecting again",
  network_error: "could not reach the provider — please try again",
  missing_params: "the provider response was incomplete — please try again",
  access_denied: "access was denied on the provider's consent screen",
};

export function AdPlatformConnections({ user, onConnectionChange, justConnected }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [oauthConfig, setOauthConfig] = useState(null);
  const [tokenStatus, setTokenStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState({});
  const [refreshing, setRefreshing] = useState({});
  const [testing, setTesting] = useState({});
  const [syncingPlatforms, setSyncingPlatforms] = useState({});
  const isDemoMode = user?.is_demo;

  const fetchTokenStatus = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/connections/token-status`, { credentials: "include", headers: authHeaders() });
      if (resp.ok) setTokenStatus(await resp.json());
    } catch (e) {
      logger.error("Failed to fetch token status:", e);
    }
  }, []);

  const fetchConnectionStatus = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/connections/status`, { credentials: "include", headers: authHeaders() });
      if (resp.ok) setConnectionStatus(await resp.json());
    } catch (e) {
      logger.error("Failed to fetch connection status:", e);
    }
  }, []);

  const fetchOAuthConfig = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/oauth/config`, { credentials: "include", headers: authHeaders() });
      if (resp.ok) setOauthConfig(await resp.json());
    } catch (e) {
      logger.error("Failed to fetch OAuth config:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  // After connecting, campaigns are enriched/synced in the background. Show a "syncing…"
  // spinner on the card until the first campaigns actually land (count > 0). Gives up after
  // ~45s so an account with genuinely zero campaigns doesn't spin forever.
  const runSyncPoll = useCallback((platformKey) => {
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const resp = await fetch(`${API}/connections/status`, { credentials: "include", headers: authHeaders() });
        if (resp.ok) {
          const data = await resp.json();
          setConnectionStatus(data);
          if ((data?.[platformKey]?.campaign_count || 0) > 0) {
            setSyncingPlatforms((prev) => ({ ...prev, [platformKey]: false }));
            fetchTokenStatus();
            return;
          }
        }
      } catch { /* keep polling */ }
      if (attempts >= 15) {
        setSyncingPlatforms((prev) => ({ ...prev, [platformKey]: false }));
        return;
      }
      setTimeout(tick, 3000);
    };
    setTimeout(tick, 1500);
  }, [fetchTokenStatus]);

  useEffect(() => {
    fetchConnectionStatus();
    fetchOAuthConfig();
    fetchTokenStatus();
  }, [fetchConnectionStatus, fetchOAuthConfig, fetchTokenStatus]);

  // Handle the OAuth redirect params (runs on mount + when the params change)
  useEffect(() => {
    const connected = searchParams.get("connected");
    const oauthSuccess = searchParams.get("oauth_success");
    const oauthError = searchParams.get("oauth_error");
    const platform = searchParams.get("platform");
    const error = searchParams.get("error");

    if (oauthSuccess === "true" && platform) {
      toast.success(`${PLATFORMS[platform]?.name || platform} connected! Syncing campaigns…`);
      setSearchParams({});
      setSyncingPlatforms((p) => ({ ...p, [platform]: true }));
      fetchConnectionStatus();
      fetchTokenStatus();
      runSyncPoll(platform);
      onConnectionChange?.();
    } else if (oauthError && platform) {
      const detail = searchParams.get("detail");
      const msg = OAUTH_ERROR_LABELS[oauthError] || oauthError.replace(/\+/g, " ");
      const suffix = detail ? ` [${decodeURIComponent(detail)}]` : "";
      toast.error(`${PLATFORMS[platform]?.name || platform} connection failed: ${msg}${suffix}`, { duration: 15000 });
      setSearchParams({});
    } else if (connected) {
      toast.success(`${PLATFORMS[connected]?.name || connected} connected!`);
      setSearchParams({});
      fetchConnectionStatus();
      fetchTokenStatus();
      onConnectionChange?.();
    } else if (error) {
      toast.error(`Connection failed: ${error}`);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, fetchConnectionStatus, fetchTokenStatus, runSyncPoll, onConnectionChange]);

  // Refresh connection/token status when returning to this tab (e.g. after connecting)
  useEffect(() => {
    const refresh = () => { fetchConnectionStatus(); fetchTokenStatus(); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [fetchConnectionStatus, fetchTokenStatus]);

  // The parent (Campaigns) intercepts the OAuth ?oauth_success param and clears it while this
  // component is still behind its loading gate, so it hands us the just-connected platform via
  // a prop. Kick off the "syncing…" spinner + poll for it.
  useEffect(() => {
    if (!justConnected) return;
    setSyncingPlatforms((p) => ({ ...p, [justConnected]: true }));
    fetchConnectionStatus();
    fetchTokenStatus();
    runSyncPoll(justConnected);
  }, [justConnected, fetchConnectionStatus, fetchTokenStatus, runSyncPoll]);

  const handleConnect = async (platformKey) => {
    const cfg = PLATFORMS[platformKey];
    if (!cfg?.enabled) {
      toast.info(`${cfg?.name || platformKey} integration coming soon.`);
      return;
    }

    if (isDemoMode) {
      setConnecting((prev) => ({ ...prev, [platformKey]: true }));
      try {
        const resp = await fetch(`${API}/connect/demo/${platformKey}`, {
          method: "POST", credentials: "include",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ account_name: `Demo ${cfg.name} Account` }),
        });
        if (resp.ok) {
          toast.success(`${cfg.name} connected! (Demo Mode)`);
          await fetchConnectionStatus();
          await fetchTokenStatus();
          onConnectionChange?.();
        } else {
          toast.error("Failed to connect in demo mode");
        }
      } catch {
        toast.error("Failed to connect");
      } finally {
        setConnecting((prev) => ({ ...prev, [platformKey]: false }));
      }
      return;
    }

    const isConfigured = oauthConfig?.[platformKey]?.is_configured ?? cfg.enabled;
    if (!isConfigured) {
      toast.error(`${cfg.name} is not yet configured. Please contact the admin.`);
      return;
    }

    setConnecting((prev) => ({ ...prev, [platformKey]: true }));
    try {
      const resp = await fetch(`${API}/oauth/connect/${platformKey}`, { credentials: "include", headers: authHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        // Same-tab, top-level redirect: never blocked by popup blockers, and the app
        // session stays intact when the user returns from the provider (a new tab could
        // come back in a logged-out state, hiding the freshly-connected account).
        window.location.href = data.auth_url;
        return;
      }
      const err = await resp.json().catch(() => ({}));
      toast.error(err.detail || "Failed to initiate connection");
    } catch {
      toast.error("Failed to connect");
    } finally {
      setConnecting((prev) => ({ ...prev, [platformKey]: false }));
    }
  };

  const handleDisconnect = async (platformKey) => {
    try {
      const resp = await fetch(`${API}/oauth/disconnect/${platformKey}`, {
        method: "POST",
        credentials: "include",
      });
      if (resp.ok) {
        toast.success(`${PLATFORMS[platformKey].name} disconnected`);
        fetchConnectionStatus();
        fetchTokenStatus();
        onConnectionChange?.();
      } else {
        toast.error("Failed to disconnect");
      }
    } catch {
      toast.error("Failed to disconnect");
    }
  };

  const handleRefreshToken = async (platformKey) => {
    setRefreshing((prev) => ({ ...prev, [platformKey]: true }));
    try {
      const resp = await fetch(`${API}/oauth/refresh/${platformKey}`, {
        method: "POST",
        credentials: "include",
      });
      if (resp.ok) {
        toast.success("Token refreshed!");
        fetchTokenStatus();
        fetchConnectionStatus();
      } else {
        toast.error("Refresh failed — please reconnect");
      }
    } catch {
      toast.error("Failed to refresh token");
    } finally {
      setRefreshing((prev) => ({ ...prev, [platformKey]: false }));
    }
  };

  const handleTestConnection = async (platformKey) => {
    setTesting((prev) => ({ ...prev, [platformKey]: true }));
    try {
      const resp = await fetch(`${API}/campaigns/test-connection/${platformKey}`, {
        method: "POST", credentials: "include",
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.success) {
          const campSteps = data.steps.filter(s => s.campaigns);
          const totalCamps = campSteps.reduce((sum, s) => sum + (s.campaigns?.length || 0), 0);
          toast.success(`Connection OK! Found ${totalCamps} campaigns. Click Sync to download.`);
        } else {
          const failedStep = data.steps.find(s => !s.ok);
          toast.error(`Connection issue: ${failedStep?.detail || "Unknown error"}${data.action ? `. Action: ${data.action}` : ""}`);
        }
      } else {
        toast.error("Test failed");
      }
    } catch {
      toast.error("Test failed");
    } finally {
      setTesting((prev) => ({ ...prev, [platformKey]: false }));
    }
  };

  const handleSyncCampaigns = async () => {
    setSyncing(true);
    try {
      const resp = await fetch(`${API}/campaigns/sync-all`, {
        method: "POST",
        credentials: "include",
      });
      if (resp.ok) {
        const data = await resp.json();
        const total = data.total_synced || 0;
        const platforms = (data.results || []).filter(r => r.success).map(r => `${r.platform} (${r.synced_count})`).join(", ");
        const failed = (data.results || []).filter(r => !r.success);
        if (total > 0) {
          toast.success(`Synced ${total} campaigns: ${platforms}`);
        } else if (failed.length > 0) {
          toast.error(`Sync failed: ${failed.map(f => `${f.platform}: ${f.error}`).join(", ")}`);
        } else {
          toast.info("No campaigns found on connected platforms");
        }
        fetchConnectionStatus();
        onConnectionChange?.();
      } else {
        const err = await resp.json();
        toast.error(err.detail || "Sync failed");
      }
    } catch {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Card className="bg-[#121212] border-white/10">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-zinc-400 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const hasAnyConnection = Object.keys(PLATFORMS).some(
    (p) => connectionStatus?.[p]?.connected
  );

  const renderPlatformCard = (platformKey) => {
    const platform = PLATFORMS[platformKey];
    const status = connectionStatus?.[platformKey];
    const token = tokenStatus?.[platformKey];
    const isConnected = status?.connected;
    const isConnecting = connecting[platformKey];
    const isRefreshing = refreshing[platformKey];
    const isConfigured = oauthConfig?.[platformKey]?.is_configured ?? platform.enabled;
    const Icon = platform.icon;

    const isExpired = token?.status === "expired";
    const isExpiringSoon = token?.status === "expiring_soon";
    const isDemo = token?.is_demo || status?.is_demo;
    const campaignCount = status?.campaign_count || 0;
    const isSyncing = !!syncingPlatforms[platformKey];

    return (
      <motion.div
        key={platformKey}
        data-testid={`campaign-platform-card-${platformKey}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: Object.keys(PLATFORMS).indexOf(platformKey) * 0.05 }}
        className={cn(
          "p-4 rounded-xl border transition-colors",
          isConnected ? platform.connectedBg : "bg-white/5 border-white/10",
          isExpired && "border-red-500/30",
          isExpiringSoon && "border-amber-500/30",
          !platform.enabled && !isConnected && "opacity-60"
        )}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={cn("w-12 h-12 rounded-full flex items-center justify-center", isConnected ? platform.iconBg : "bg-white/5")}>
              <Icon className={cn("w-6 h-6", platform.iconColor)} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-white font-semibold">{platform.name}</h3>
                {isConnected ? (
                  <Badge className="bg-emerald-500/10 text-emerald-400">Connected</Badge>
                ) : !platform.enabled ? (
                  <Badge className="bg-zinc-500/10 text-zinc-400">Coming Soon</Badge>
                ) : (
                  <Badge className="bg-zinc-500/10 text-zinc-400">Not Connected</Badge>
                )}
                {isConnected && !isDemo && isExpired && (
                  <Badge className="bg-red-500/10 text-red-400">
                    <AlertTriangle className="w-3 h-3 mr-1" /> Token Expired
                  </Badge>
                )}
                {isConnected && !isDemo && isExpiringSoon && (
                  <Badge className="bg-amber-500/10 text-amber-400">
                    <Clock className="w-3 h-3 mr-1" /> Expiring Soon
                  </Badge>
                )}
              </div>
              {isConnected ? (
                <div className="text-sm text-zinc-400 mt-1">
                  <p>{status.account_name || "Connected Account"}</p>
                  <p className="flex items-center gap-1 text-xs text-zinc-400 mt-0.5">
                    <Clock className="w-3 h-3" />
                    Last synced: {formatDate(status.last_synced)}
                    {token?.hours_remaining && !isDemo && (
                      <span className={cn("ml-2", isExpiringSoon ? "text-amber-400" : "text-zinc-400")}>
                        ({token.hours_remaining}h until token expires)
                      </span>
                    )}
                  </p>
                  <p
                    data-testid={`campaign-connection-count-${platformKey}`}
                    className="flex items-center gap-1.5 text-xs mt-0.5"
                  >
                    {isSyncing ? (
                      <>
                        <Loader2 className="w-3 h-3 text-blue-400 animate-spin" />
                        <span className="text-blue-400 font-medium">Syncing campaigns…</span>
                      </>
                    ) : campaignCount > 0 ? (
                      <>
                        <CheckCircle className="w-3 h-3 text-emerald-400" />
                        <span className="text-emerald-400 font-medium">
                          {campaignCount} campaign{campaignCount === 1 ? "" : "s"} pulled
                        </span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-3 h-3 text-zinc-500" />
                        <span className="text-zinc-500">
                          No campaigns pulled yet — click Sync
                        </span>
                      </>
                    )}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-zinc-400">{platform.description}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isConnected && !isDemo && (isExpired || isExpiringSoon) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRefreshToken(platformKey)}
                disabled={isRefreshing}
                className={cn(
                  "border-amber-500/30 text-amber-300 hover:bg-amber-500/10",
                  isExpired && "border-red-500/30 text-red-300 hover:bg-red-500/10"
                )}
              >
                {isRefreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Refresh
              </Button>
            )}
            {isConnected && !isDemo ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTestConnection(platformKey)}
                  disabled={testing[platformKey]}
                  className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10"
                >
                  {testing[platformKey] ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-1" />}
                  Test
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDisconnect(platformKey)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Unlink className="w-4 h-4 mr-1" /> Disconnect
                </Button>
              </>
            ) : isConnected && isDemo ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDisconnect(platformKey)}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <Unlink className="w-4 h-4 mr-1" /> Disconnect
              </Button>
            ) : platform.enabled && isConfigured ? (
              <Button
                data-testid={`campaign-connect-${platformKey}-btn`}
                onClick={() => handleConnect(platformKey)}
                disabled={isConnecting}
                className={platform.buttonClass}
              >
                {isConnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                Connect
              </Button>
            ) : (
              <Button size="sm" disabled className="bg-zinc-700/50 text-zinc-400 cursor-not-allowed">
                <Lock className="w-3 h-3 mr-1" />
                {platform.enabled ? "Not Configured" : "Coming Soon"}
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <Card data-testid="campaign-platform-connections" className="bg-[#121212] border-white/10">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-white flex items-center gap-2">
            <Link2 className="w-5 h-5 text-blue-400" />
            Ad Platform Connections
            {isDemoMode ? (
              <Badge className="bg-amber-500/10 text-amber-400 ml-2"><Sparkles className="w-3 h-3 mr-1" /> Demo Mode</Badge>
            ) : (
              <Badge className="bg-emerald-500/10 text-emerald-400 ml-2">Live Mode</Badge>
            )}
          </CardTitle>
          <CardDescription className="text-zinc-400">
            {isDemoMode
              ? "Connect demo accounts to test with simulated campaign data"
              : "Connect your real ad accounts via OAuth to sync live campaign data"}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Object.keys(PLATFORMS).filter(k => !PLATFORMS[k].hidden && (!PLATFORMS[k].demoOnly || isDemoMode)).map(renderPlatformCard)}
        </div>

        {!hasAnyConnection && (
          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 mt-0.5 text-amber-400" />
              <div className="text-sm">
                <p className="font-medium text-amber-300">Connect your ad accounts</p>
                <p className="mt-1 text-amber-300/70">
                  Click Connect to link your ad account and start syncing campaign data.
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
